import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Grid,
  TypographyProps,
  Stack,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Badge,
} from '@mui/material';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import moment from 'moment';

import { ISubTopic } from '../../../../store/SubTopicSlice';
import DetailViewCard from '../../../../components/Card/DetailViewCard';
import CustomGrayBoxIconButton from '../../../../components/Button/CustomGrayBoxIconButton';

export interface ISubTopicListItem {
  subTopic: ISubTopic;
  toggleViewLogger: (subTopic: ISubTopic) => void;
  handleDelete: (subTopic: ISubTopic) => void;
  handleUpdate: (subTopic: ISubTopic) => void;
  isDeletable?: boolean;
  isEditable?: boolean;
}

const titleProps: TypographyProps = {
  variant: 'caption',
  className: 'ellipsis-text',
  sx: {
    color: 'text.primary',
  },
};

const descriptionProps: TypographyProps = {
  className: 'ellipsis-text',
  variant: 'subtitle2',
  fontWeight: 600,
  sx: {
    color: 'text.primary',
  },
};

const SubTopicListItem: React.FC<ISubTopicListItem> = ({
  subTopic,
  toggleViewLogger,
  handleDelete,
  handleUpdate,
  isDeletable,
  isEditable,
}) => {
  const navigate = useNavigate();

  const createdAt = moment(subTopic.createdAt).fromNow();
  const updatedAt = moment(subTopic.updatedAt).fromNow();
  const menuId = `option-menu-${subTopic._id}`;
  const buttonId = `option-button-${subTopic._id}`;

  // State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Card elevation={3} sx={{ mb: '15px' }}>
        <CardContent
          className="btn-no-style"
          style={{ padding: '15px' }}
          onClick={() => {
            navigate(
              `${process.env.PUBLIC_URL}/dashboard/question-bank/questions/${subTopic._id}`
            );
          }}
        >
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={6} md={6} lg={4}>
              <Badge
                badgeContent={subTopic.sortingOrder || 0}
                color="info"
                style={{ width: '100%' }}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <DetailViewCard
                  title="Sub Topic"
                  titleProps={titleProps}
                  description={subTopic.name}
                  descriptionProps={descriptionProps}
                  containerProps={{ width: '100%' }}
                />
              </Badge>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={2}>
              <DetailViewCard
                title="Created By"
                titleProps={titleProps}
                description={
                  subTopic.createdBy
                    ? `${subTopic.createdBy.firstname}${
                        subTopic.createdBy.lastname
                          ? ` ${subTopic.createdBy.firstname}`
                          : ''
                      }`
                    : '-'
                }
                descriptionProps={descriptionProps}
              />
            </Grid>
            <Grid item xs={12} sm={5} md={5} lg={2}>
              <DetailViewCard
                title="Created At"
                titleProps={titleProps}
                description={createdAt}
                descriptionProps={descriptionProps}
              />
            </Grid>
            <Grid item xs={12} sm={5} md={5} lg={2}>
              <DetailViewCard
                title="Last Modified"
                titleProps={titleProps}
                description={updatedAt}
                descriptionProps={descriptionProps}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={2} lg={2}>
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
              >
                {isEditable && (
                  <CustomGrayBoxIconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      handleUpdate(subTopic);
                      handleMenuClose();
                    }}
                  >
                    <ModeEditOutlineOutlinedIcon />
                  </CustomGrayBoxIconButton>
                )}
                <CustomGrayBoxIconButton
                  id={buttonId}
                  sx={{ ml: isEditable ? '10px' : '' }}
                  onClick={handleMenuOpen}
                >
                  <MoreHorizOutlinedIcon />
                </CustomGrayBoxIconButton>
                <Menu
                  id={menuId}
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  MenuListProps={{
                    'aria-labelledby': buttonId,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleViewLogger(subTopic);
                      handleMenuClose();
                    }}
                  >
                    View Log
                  </MenuItem>
                  {isDeletable && (
                    <MenuItem
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(subTopic);
                        handleMenuClose();
                      }}
                    >
                      Delete
                    </MenuItem>
                  )}
                </Menu>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubTopicListItem;
